/**
 * Calculate dynamic package labels based on PAX number and categories data
 * @param {Array} categoriesData - Array of category objects with choose_rule
 * @param {number} paxNumber - Selected PAX number from filters
 * @returns {string} - Formatted string like "2x Salads, 1x Appetizers"
 */
export const calculatePackageLabels = (categoriesData, paxNumber) => {
  if (!categoriesData || !Array.isArray(categoriesData) || !paxNumber) {
    return '';
  }

  const labels = [];

  categoriesData.forEach(category => {
    if (!category.choose_rule || !Array.isArray(category.choose_rule)) {
      return;
    }

    // Find the appropriate choose rule for the given pax number
    // Sort rules by pax descending and find the first one where paxNumber >= rule.pax
    const sortedRules = [...category.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
    const matchingRule = sortedRules.find(rule => 
      paxNumber >= parseInt(rule.pax)
    );

    if (matchingRule && matchingRule.choose > 0) {
      const categoryName = category.name_en || category.name_ar || 'Items';
      labels.push(`${matchingRule.choose}x ${categoryName}`);
    }
  });

  return labels.join(', ');
};

/**
 * Get individual category limits for a specific PAX number
 * @param {Array} categoriesData - Array of category objects with choose_rule
 * @param {number} paxNumber - Selected PAX number from filters
 * @returns {Array} - Array of objects with category info and limits
 */
export const getCategoryLimitsForPax = (categoriesData, paxNumber) => {
  if (!categoriesData || !Array.isArray(categoriesData) || !paxNumber) {
    return [];
  }

  const categoryLimits = [];

  categoriesData.forEach(category => {
    if (!category.choose_rule || !Array.isArray(category.choose_rule)) {
      return;
    }

    // Find the appropriate choose rule for the given pax number
    // Sort rules by pax descending and find the first one where paxNumber >= rule.pax
    const sortedRules = [...category.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
    const matchingRule = sortedRules.find(rule => 
      paxNumber >= parseInt(rule.pax)
    );

    if (matchingRule && matchingRule.choose > 0) {
      categoryLimits.push({
        categoryId: category.id,
        categoryName: category.name_en || category.name_ar || 'Items',
        itemLimit: matchingRule.choose,
        paxRangeFrom: parseInt(matchingRule.pax),
        paxRangeTo: parseInt(matchingRule.pax) // In new structure, each rule is for a specific pax
      });
    }
  });

  return categoryLimits;
};

/**
 * Validate if all required package items have been added based on categories structure
 * @param {Object} packageData - Package object containing categories and items
 * @param {number} paxNumber - Selected PAX number from filters
 * @returns {Object} - Validation result with isValid flag and missing categories info
 */
export const validatePackageCompletion = (packageData, paxNumber) => {
  if (!packageData || !paxNumber) {
    return { 
      isValid: false, 
      missingCategories: [],
      message: 'Invalid package data or PAX number' 
    };
  }

  const { categories: categoriesData, items: packageItems } = packageData;

  if (!categoriesData || !Array.isArray(categoriesData)) {
    return { 
      isValid: true, 
      missingCategories: [],
      message: 'No categories defined - package is valid' 
    };
  }

  const missingCategories = [];
  let totalRequiredItems = 0;
  let totalAddedItems = 0;

  // Check each category
  categoriesData.forEach(category => {
    // Support both choose_rule and selection_rules naming conventions
    const rules = category.choose_rule || category.selection_rules;
    if (!rules || !Array.isArray(rules)) {
      return;
    }

    let matchingRule = null;
    let requiredItemsForCategory = 0;

    // Handle different rule structures
    if (category.selection_rules) {
      // New structure with pax_range and max_selections
      const sortedRules = [...rules].sort((a, b) => 
        parseInt(b.pax_range?.min || 0) - parseInt(a.pax_range?.min || 0)
      );
      matchingRule = sortedRules.find(rule => 
        paxNumber >= parseInt(rule.pax_range?.min || 0) && 
        paxNumber <= parseInt(rule.pax_range?.max || 999)
      );
      requiredItemsForCategory = matchingRule ? parseInt(matchingRule.max_selections || 0) : 0;
    } else {
      // Old structure with pax and choose
      const sortedRules = [...rules].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      matchingRule = sortedRules.find(rule => 
        paxNumber >= parseInt(rule.pax)
      );
      requiredItemsForCategory = matchingRule ? parseInt(matchingRule.choose || 0) : 0;
    }

    if (matchingRule && requiredItemsForCategory > 0) {
      totalRequiredItems += requiredItemsForCategory;
      
      // Debug logging for PAX-based requirements
      const ruleDescription = category.selection_rules 
        ? `PAX ${matchingRule.pax_range?.min}-${matchingRule.pax_range?.max} -> ${matchingRule.max_selections}`
        : `PAX ${matchingRule.pax} -> ${matchingRule.choose}`;
      console.log(`PAX ${paxNumber} - Category "${category.name_en}": requires ${requiredItemsForCategory} items (rule: ${ruleDescription})`);

      // Count items added for this category
      const itemsInCategory = packageItems ? packageItems.filter(item => 
        item.category_id === category.id
      ) : [];
      
      // Debug logging - remove this once validation is working
      if (process.env.NODE_ENV === 'development') {
        console.log('Validation Debug - Category Check:', {
          categoryId: category.id,
          categoryName: category.name_en,
          paxNumber: paxNumber,
          requiredItems: requiredItemsForCategory,
          itemsInCategory: itemsInCategory.length,
          itemsInCategoryDetails: itemsInCategory.map(item => ({
            id: item.id,
            name: item.name || item.name_en,
            category_id: item.category_id,
            quantity: item.quantity,
            has_variants: item.has_variants
          }))
        });
      }

      // Calculate total quantity for this category (including variant items)
      const totalCategoryQuantity = itemsInCategory.reduce((total, item) => {
        if (item.has_variants && item.items) {
          // For variant items, sum up all variant quantities
          return total + item.items.reduce((variantTotal, variantItem) => 
            variantTotal + (variantItem.quantity || 0), 0
          );
        } else {
          // For regular items, just add the quantity
          return total + (item.quantity || 0);
        }
      }, 0);

      totalAddedItems += totalCategoryQuantity;

      // Check if this category has enough items
      if (totalCategoryQuantity < requiredItemsForCategory) {
        missingCategories.push({
          categoryId: category.id,
          categoryName: category.name_en || category.name_ar || 'Items',
          required: requiredItemsForCategory,
          added: totalCategoryQuantity,
          missing: requiredItemsForCategory - totalCategoryQuantity
        });
      }
    }
  });

  const isValid = missingCategories.length === 0;
  
  return {
    isValid,
    missingCategories,
    totalRequiredItems,
    totalAddedItems,
    currentPax: paxNumber,
    message: isValid 
      ? `All required items have been added for ${paxNumber} PAX`
      : `Missing items in ${missingCategories.length} categories for ${paxNumber} PAX`
  };
};